import { App } from '@capacitor/app';

import { appendAppLog } from '../platform/appLog';
import { leseEinstellungen, schreibeEinstellung } from '../platform/einstellungen';
import { leseQrUeberschreibungen } from '../persistence/qrUeberschreibungen';
import type { Store } from '../persistence/port';
import { ladeBeispielpruefungen } from './zugang';

/**
 * Die Beispielprüfung auf Wunsch der Einrichtung — ein Schalter in den iPad-Einstellungen.
 *
 * **Auftrag des Nutzers, 2026-09-19:** Das Einlesen gehört nicht mehr in die App. Es steht
 * ausschliesslich in den iPad-Einstellungen unter `demo_install`; die App führt es aus, sobald
 * sie wieder in den Vordergrund kommt, und legt den Schalter danach zurück.
 *
 * **Warum dort und nicht hier:** dieselbe Begründung wie beim Serverzugang — was der
 * Einrichtung gehört, steht an einer Stelle, an die der Prüfer während einer Prüfung nicht
 * kommt (der geführte Zugriff sperrt den Weg in die App-Einstellungen). Ein Knopf im Dialog
 * liess sich mitten in einer Prüfung drücken und ersetzte dabei die Prüfung, an der gerade
 * gearbeitet wurde.
 */

/** Was die Wache zum Arbeiten braucht. Eingespritzt, damit sie ohne Gerät prüfbar bleibt. */
export interface DemoDeps {
  /** Der geltende Stand des Schalters samt Herkunft — aus der Rangfolge in `platform/einstellungen.ts`. */
  lies: () => Promise<{ wert: boolean; quelle: string }>;
  /** Liest die Beispielprüfungen ein und meldet, wie viele es waren. */
  installiere: () => Promise<number>;
  /** Legt den Schalter in den iPad-Einstellungen zurück auf „aus". */
  zuruecksetzen: () => Promise<void>;
  /**
   * Das Dateilog. `stufe` bestimmt, unter welcher Konsolenfunktion die Zeile am Kabel in
   * Xcode erscheint (s. `platform/appLog.ts`); in der Datei sieht jede Zeile gleich aus.
   */
  log: (zeile: string, stufe?: 'warn') => Promise<void> | void;
}

/**
 * Einmal nachsehen und gegebenenfalls einlesen.
 *
 * Gibt die Zahl der eingelesenen Prüfungen zurück — oder `null`, wenn nichts zu tun war.
 */
export async function demoAufWunsch(deps: DemoDeps): Promise<number | null> {
  const { wert, quelle } = await deps.lies();
  if (!wert) return null;

  /*
   * ⚠️ **Nur der Schalter in den iPad-Einstellungen zählt.** Stünde der Wert im MDM-Profil oder
   * in einem gescannten Einstellungscode, käme er nach dem Zurücklegen sofort wieder — beide
   * stehen in der Rangfolge über den iPad-Einstellungen. Die App liese die Beispielprüfung dann
   * bei **jedem** Vordergrundwechsel neu ein und ersetzte dabei jedes Mal die Bewertungen daran.
   */
  if (quelle !== 'ipad') {
    await deps.log(
      'Demo: Schalter steht auf an, kommt aber aus „' + quelle + '" statt aus den ' +
        'iPad-Einstellungen — nicht eingelesen. Ein Wert von dort liesse sich nicht zurücklegen.',
      'warn',
    );
    return null;
  }

  let anzahl: number;
  try {
    anzahl = await deps.installiere();
  } catch (fehler) {
    // ⚠️ **Der Schalter bleibt an.** Der Wunsch ist nicht erledigt, also darf er nicht
    // verschwinden — der nächste Vordergrundwechsel versucht es erneut.
    await deps.log('Demo: Einlesen fehlgeschlagen, Schalter bleibt an: ' + String(fehler), 'warn');
    return null;
  }

  await deps.zuruecksetzen();
  await deps.log('Demo: ' + String(anzahl) + ' Beispielprüfungen eingelesen, Schalter zurückgelegt.');
  return anzahl;
}

/**
 * Ein Lauf zur Zeit — ein zweiter Ruf fällt weg, solange der erste arbeitet.
 *
 * ⚠️ **Am Simulator gesehen, 2026-09-19.** Beim Kaltstart feuert iOS `appStateChange` mit
 * `isActive === true` sofort. Der Lauf beim Start und der aus dem Horcher überlappten sich,
 * beide begannen einen Import, und der zweite lief in den Transaktionswächter der Ablage
 * („tx() ist nicht verschachtelbar"). Der Import selbst nahm keinen Schaden — der erste ging
 * durch —, aber im Dateilog stand ein Fehler, der keiner war.
 *
 * Bewusst **fallen lassen** statt anstellen: die beiden Rufe wollen dasselbe. Ein zweiter Lauf
 * hinterher liese die Beispielprüfung ein zweites Mal ersetzen, ohne dass jemand darum gebeten
 * hätte.
 */
export function nichtDoppelt(fn: () => Promise<unknown>): () => Promise<void> {
  let laeuft = false;
  return async () => {
    if (laeuft) return;
    laeuft = true;
    try {
      await fn();
    } finally {
      laeuft = false;
    }
  };
}

/** Die Verdrahtung mit der App: derselbe Ablauf, nur mit den echten Quellen. */
function echteDeps(store: Store, clientId: string): DemoDeps {
  return {
    lies: async () => {
      const qr = await leseQrUeberschreibungen(store, clientId).catch(() => ({}));
      const gemischt = await leseEinstellungen(undefined, qr);
      return { wert: gemischt.demo_install.wert, quelle: gemischt.demo_install.quelle };
    },
    installiere: () => ladeBeispielpruefungen(store),
    zuruecksetzen: () => schreibeEinstellung('demo_install', false),
    log: appendAppLog,
  };
}

/**
 * Beim Start einmal nachsehen und danach bei jeder Rückkehr in den Vordergrund.
 *
 * ⚠️ **Der Start zählt mit** (Entscheidung des Nutzers, 2026-09-19). Legt jemand den Schalter
 * um, während die App gar nicht läuft — von iOS beendet, Gerät neu gestartet —, gäbe es ohne
 * ihn kein `appStateChange`, und der Schalter bliebe wirkungslos an.
 *
 * ⚠️ **Nebenher, nie blockierend.** Ein Fehlschlag darf den Start nicht aufhalten; er steht im
 * Dateilog. Der zurückgegebene Abmelder gehört zum Aufräumen dazu — ein Horcher, den niemand
 * wieder abmeldet, überlebt jeden Neuaufbau der Oberfläche.
 */
export function starteDemoWache(
  store: Store,
  clientId: string,
  melde?: (anzahl: number) => void,
): () => void {
  const deps = echteDeps(store, clientId);
  const lauf = nichtDoppelt(async () => {
    try {
      const anzahl = await demoAufWunsch(deps);
      if (anzahl !== null && melde) melde(anzahl);
    } catch (fehler) {
      await appendAppLog('Demo: Schalter nicht auswertbar: ' + String(fehler), 'warn');
    }
  });
  const einmal = () => void lauf();

  einmal();
  const horcher = App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) einmal();
  });
  return () => {
    void horcher.then((h) => h.remove()).catch(() => {
      // Abmelden scheitert nur, wenn es den Horcher nie gab — dann ist nichts aufzuräumen.
    });
  };
}
